import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { saveProspectListContext } from '@/hooks/useReturnNavigation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dashboardAPI, usersAPI } from '@/lib/api';
import { prefetchUserProfile } from '@/lib/resources';
import { getCached, setCached, isFresh, swrFetch } from '@/lib/dataCache';
import {
    Trophy, Loader2, Clock, AlertTriangle, XCircle, RotateCcw,
    ChevronDown, ChevronRight, Users, Wallet, Search, ArrowLeft, ArrowUpDown, UserRound,
    Mail, Phone, PackageCheck, Hash, Target, ArrowRight, TrendingUp
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import ParrainCommissionFeed from '@/components/dashboard/ParrainCommissionFeed';
import { HashtagFilter } from '@/components/prospect/HashtagFilter';
import { HashtagChips } from '@/components/prospect/HashtagChips';

const euro = (n) => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(n || 0);

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '—'; }
};

// Nombre de jours écoulés depuis une date ISO (arrondi, min 0)
const daysSince = (iso) => {
    if (!iso) return null;
    try {
        const diff = Date.now() - new Date(iso).getTime();
        return Math.max(0, Math.floor(diff / 86400000));
    } catch { return null; }
};

const initials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

// ---- Avatar chip ----
function PersonChip({ person, active, badge, onClick, testid }) {
    if (!person) return null;
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`group flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 transition-all
                ${active ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-300' : 'border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/40'}`}
        >
            <Avatar className="h-7 w-7 border border-slate-200">
                {person.avatar && <AvatarImage src={person.avatar} alt={person.name} />}
                <AvatarFallback className="bg-purple-100 text-purple-700 text-[11px] font-semibold">
                    {initials(person.name)}
                </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-slate-700 max-w-[140px] truncate">{person.name}</span>
            {typeof badge === 'number' && badge > 0 && (
                <span className="text-[11px] font-semibold text-purple-700 bg-purple-100 rounded-full px-1.5 py-0.5">{badge}</span>
            )}
        </button>
    );
}

// ---- Bouton "Voir la fiche" (navigue vers le profil équipe + précharge au survol) ----
function FicheButton({ onClick, testid, userId }) {
    return (
        <button type="button" data-testid={testid} onClick={onClick}
            onMouseEnter={() => userId && prefetchUserProfile(userId)} title="Voir la fiche"
            className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 transition-all shrink-0">
            <UserRound className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Fiche</span>
        </button>
    );
}

// ---- Détail RBAC additif (Gagné / À chercher / Perdu) avec ventilation par part ----
// Chaque colonne affiche le TOTAL (somme des parts visibles) puis le détail des parts
// que le rôle connecté a le droit de voir. Garantie : somme(apporteurs) == total équipe.
const sumBd = (items) => (items || []).reduce((s, i) => s + (i.value || 0), 0);

function BreakdownStats({ breakdown, size = 'sm', testid }) {
    const bd = breakdown || {};
    const blocks = [
        { label: 'Gagné', key: 'won', color: 'text-emerald-600' },
        { label: 'À chercher', key: 'pending', color: 'text-blue-600' },
        { label: 'Perdu', key: 'lost', color: 'text-rose-500' },
    ];
    return (
        <span className="flex items-start justify-end gap-4 sm:gap-6" data-testid={testid}>
            {blocks.map((b) => {
                const items = bd[b.key] || [];
                const total = sumBd(items);
                return (
                    <span key={b.key} className="text-right leading-tight" data-testid={`bd-${b.key}`}>
                        <span className="block text-[9px] uppercase tracking-wide text-slate-400">{b.label}</span>
                        <span className={`block ${size === 'lg' ? 'text-base sm:text-lg' : 'text-xs sm:text-sm'} font-bold ${b.color}`}>{euro(total)}</span>
                        {items.length > 1 && items.map((it) => (
                            <span key={it.key} className="block text-[10px] text-slate-500 whitespace-nowrap">
                                {it.label} <span className="font-semibold text-slate-600">{euro(it.value)}</span>
                            </span>
                        ))}
                    </span>
                );
            })}
        </span>
    );
}

// Y a-t-il au moins une valeur dans un breakdown (won/pending/lost) ?
const hasBd = (bd) => !!bd && ['won', 'pending', 'lost'].some((k) => (bd[k] || []).length > 0);
// Un dossier a-t-il déjà une commission chiffrée (part visible > 0) ?
const dossierHasCommission = (d) => Array.isArray(d.commissions) && d.commissions.some((c) => (c?.value || 0) > 0);

// ---- Summary metric card ----
const BUCKET_META = {
    won: { label: 'Gagnés', icon: Trophy, color: 'emerald' },
    awaiting_install: { label: "En attente d'installation", icon: PackageCheck, color: 'indigo' },
    pending: { label: 'En attente', icon: Wallet, color: 'blue' },
    in_progress: { label: 'En cours', icon: Clock, color: 'blue' },
    overdue: { label: 'En retard', icon: AlertTriangle, color: 'amber' },
    lost: { label: 'Perdus', icon: XCircle, color: 'rose' },
    auto_lost: { label: 'Perdus (inactivité)', icon: RotateCcw, color: 'red' },
};
const COLOR_CLASSES = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    red: 'bg-red-50 text-red-700 border-red-200',
};

function SummaryCard({ bucketKey, value, sub, activeBucket, onClick, hasDetail }) {
    const meta = BUCKET_META[bucketKey];
    const Icon = meta.icon;
    const isActive = activeBucket === bucketKey;
    return (
        <button
            type="button"
            data-testid={`summary-card-${bucketKey}`}
            onClick={onClick}
            disabled={!hasDetail}
            className={`flex flex-col items-start gap-1 rounded-2xl border p-4 text-left transition-all
                ${COLOR_CLASSES[meta.color]}
                ${hasDetail ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'cursor-default'}
                ${isActive ? 'ring-2 ring-offset-1 ring-purple-400 shadow-md' : ''}`}
        >
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">{meta.label}</span>
            </div>
            <span className="text-2xl font-bold text-slate-900">{value}</span>
            {sub != null && <span className="text-xs font-medium opacity-80">{sub}</span>}
        </button>
    );
}

// ---- Dossier line card ----
const DossierCard = memo(function DossierCard({ d, onOpen, onTagClick }) {
    const isEP = d.pipeline === 'easy_power';
    return (
        <button
            type="button"
            data-testid={`dossier-card-${d.id}-${d.pipeline}`}
            onClick={() => onOpen(d.id, d.pipeline)}
            className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-purple-300 hover:shadow-sm transition-all"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 truncate">{d.company_name}</span>
                        <Badge className={`shrink-0 ${isEP ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'} hover:opacity-90`}>
                            {isEP ? 'Courtage' : 'Travaux d’optimisation'}
                        </Badge>
                        {d.origin === 'parrainage' && (
                            <Badge className="shrink-0 bg-indigo-100 text-indigo-800 border border-indigo-200 hover:opacity-90" data-testid={`dossier-parrainage-badge-${d.id}`}>
                                Parrainage
                            </Badge>
                        )}
                    </div>
                    {d.contact_name && d.contact_name !== d.company_name && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{d.contact_name}</p>
                    )}
                    <HashtagChips ids={d.hashtags} max={3} className="mt-1" onTagClick={onTagClick} />
                </div>
                {Array.isArray(d.commissions) && d.commissions.length > 0 && (
                    <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`dossier-commissions-${d.id}`}>
                        {d.commissions.map((c, i) => (
                            <div key={i} className="text-right leading-tight">
                                <p className="text-[10px] uppercase tracking-wide text-slate-400">{c.label}</p>
                                <p className="text-sm font-bold text-slate-800">{euro(c.value)}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-0.5">
                    <span className="font-medium text-slate-700">Étape :</span> {d.stage_label}
                    {d.stage_changed_at && (
                        <span className="text-slate-400">· depuis le {fmtDate(d.stage_changed_at)}{d.days_in_stage != null ? ` (${d.days_in_stage} j)` : ''}</span>
                    )}
                </span>
                <span className="inline-flex items-center gap-1">
                    <span className="font-medium text-slate-700">Créé le</span> {fmtDate(d.created_at)}
                    {daysSince(d.created_at) != null && <span className="text-slate-400">· il y a {daysSince(d.created_at)} j</span>}
                </span>
                {isEP && d.etude_recue_at && (
                    <span><span className="font-medium text-slate-700">Étude :</span> {fmtDate(d.etude_recue_at)}</span>
                )}
                {d.bucket === 'overdue' && d.overdue_days != null && (
                    <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                        <AlertTriangle className="h-3 w-3" /> Retard de {d.overdue_days} j
                    </span>
                )}
            </div>

            {(d.bucket === 'lost' || d.bucket === 'auto_lost') && d.loss_reason && (
                <p className="mt-2 text-xs text-rose-700 bg-rose-50 rounded-md px-2 py-1">
                    <span className="font-semibold">Raison :</span> {d.loss_reason}
                </p>
            )}

            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                {d.apporteur && (
                    <span className="inline-flex items-center gap-1.5">
                        <Avatar className="h-5 w-5">
                            {d.apporteur.avatar && <AvatarImage src={d.apporteur.avatar} />}
                            <AvatarFallback className="bg-slate-100 text-slate-600 text-[9px]">{initials(d.apporteur.name)}</AvatarFallback>
                        </Avatar>
                        {d.apporteur.name}
                    </span>
                )}
                {d.vp && <span className="text-slate-400">VP : {d.vp.name}</span>}
            </div>
        </button>
    );
});

// ---- En-tête profil (issue 3) : photo, email, téléphone d'un apporteur / VP ciblé ----
function ScopeProfileHeader({ profile, onOpenFiche }) {
    if (!profile) return null;
    const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    const roleLabel = profile.role === 'vp' ? 'VP' : profile.role === 'apporteur' ? 'Apporteur' : profile.role;
    return (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="scope-profile-header">
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
                <Avatar className="h-14 w-14 border border-purple-200">
                    {profile.profile_picture && <AvatarImage src={profile.profile_picture} alt={name} />}
                    <AvatarFallback className="bg-purple-100 text-purple-700 text-lg font-semibold">{initials(name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-slate-900 truncate" data-testid="scope-profile-name">{name}</h3>
                        <Badge className="bg-purple-100 text-purple-700 hover:opacity-90">{roleLabel}</Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                        {profile.email && (
                            <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-1.5 hover:text-purple-700 transition-colors" data-testid="scope-profile-email">
                                <Mail className="h-3.5 w-3.5 text-slate-400" /> {profile.email}
                            </a>
                        )}
                        {profile.phone && (
                            <a href={`tel:${profile.phone}`} className="inline-flex items-center gap-1.5 hover:text-purple-700 transition-colors" data-testid="scope-profile-phone">
                                <Phone className="h-3.5 w-3.5 text-slate-400" /> {profile.phone}
                            </a>
                        )}
                        {!profile.email && !profile.phone && (
                            <span className="text-slate-400 italic">Coordonnées non renseignées</span>
                        )}
                    </div>
                </div>
                <Button
                    variant="outline" size="sm" data-testid="scope-profile-fiche-btn"
                    onClick={onOpenFiche}
                    onMouseEnter={() => prefetchUserProfile(profile.id)}
                    className="ml-auto gap-1.5 text-purple-700 border-purple-200 hover:bg-purple-50">
                    <UserRound className="h-4 w-4" /> Voir la fiche complète
                </Button>
            </CardContent>
        </Card>
    );
}

const PERIOD_TYPES = [
    { value: 'all', label: 'Toutes périodes' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'yesterday', label: 'Hier' },
    { value: 'this_week', label: 'Cette semaine' },
    { value: 'this_month', label: 'Ce mois-ci' },
    { value: 'this_year', label: 'Cette année' },
    { value: 'custom', label: 'Période personnalisée' },
];

const PIPELINE_TYPES = [
    { value: 'all', label: 'Tous les modules' },
    { value: 'easy_power', label: 'Courtage' },
    { value: 'chantier', label: 'Travaux d’optimisation' },
];

// Tri intelligent des listes de dossiers (vue détail). Le montant respecte le rôle
// connecté (champ `sort_amount` calculé côté backend) et tous les filtres actifs.
const SORT_OPTIONS = [
    { value: 'amount_desc', label: 'Montant ↓ (plus gros)' },
    { value: 'amount_asc', label: 'Montant ↑ (plus petit)' },
    { value: 'overdue_desc', label: 'Retard le + élevé' },
    { value: 'created_desc', label: 'Création (récent)' },
    { value: 'created_asc', label: 'Création (ancien)' },
    { value: 'activity_desc', label: 'Dernière activité' },
    { value: 'lost_date', label: 'Date passage perdu' },
    { value: 'stage', label: 'Étape pipeline' },
    { value: 'apporteur', label: 'Apporteur' },
    { value: 'team', label: 'Équipe' },
    { value: 'module', label: 'Module' },
];

const _s = (v) => v || '';
function sortDossiers(rows, key) {
    const r = [...(rows || [])];
    switch (key) {
        case 'amount_desc': r.sort((a, b) => (b.sort_amount || 0) - (a.sort_amount || 0)); break;
        case 'amount_asc': r.sort((a, b) => (a.sort_amount || 0) - (b.sort_amount || 0)); break;
        case 'overdue_desc': r.sort((a, b) => (b.overdue_days || 0) - (a.overdue_days || 0)); break;
        case 'created_desc': r.sort((a, b) => _s(b.created_at).localeCompare(_s(a.created_at))); break;
        case 'created_asc': r.sort((a, b) => _s(a.created_at).localeCompare(_s(b.created_at))); break;
        case 'activity_desc': r.sort((a, b) => _s(b.updated_at).localeCompare(_s(a.updated_at))); break;
        case 'lost_date': r.sort((a, b) => _s(b.stage_changed_at).localeCompare(_s(a.stage_changed_at))); break;
        case 'stage': r.sort((a, b) => _s(a.stage_label).localeCompare(_s(b.stage_label))); break;
        case 'apporteur': r.sort((a, b) => _s(a.apporteur?.name).localeCompare(_s(b.apporteur?.name))); break;
        case 'team': r.sort((a, b) => _s(a.team_name).localeCompare(_s(b.team_name))); break;
        case 'module': r.sort((a, b) => _s(a.pipeline).localeCompare(_s(b.pipeline))); break;
        default: break;
    }
    return r;
}

export default function PilotageDashboard({ user }) {
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(false);
    const [data, setData] = useState(null);
    const dataRef = useRef(false); // a-t-on déjà des données à l'écran ?
    const dataKeyRef = useRef(null); // clé (cible) des données actuellement affichées
    const autoPickRef = useRef(false); // autoriser l'auto-sélection du 1er bucket non vide (entrée scope)
    const detailRef = useRef(null); // pour scroller vers la liste au clic KPI
    const [expandedTeams, setExpandedTeams] = useState({});

    // ============================================================
    // ÉTAT DE VUE = URL (source de vérité) : chaque niveau de drill-down
    // (scope, bucket, filtres) est encodé dans l'URL → vrai historique de
    // navigation (Retour/Suivant) + restauration exacte du contexte.
    // ============================================================
    const [searchParams, setSearchParams] = useSearchParams();

    const periodType = searchParams.get('period') || 'all';
    const monthVal = searchParams.get('month') || '';
    const yearVal = searchParams.get('year') || String(new Date().getFullYear());
    const weekStart = searchParams.get('week_start') || '';
    const dateFrom = searchParams.get('from') || '';
    const dateTo = searchParams.get('to') || '';
    const pipeline = searchParams.get('pipeline') || 'all';
    const scopeType = searchParams.get('scope_type') || 'all';
    const scopeId = searchParams.get('scope_id') || null;
    const scopeName = searchParams.get('scope_name') || null;
    const scope = useMemo(() => ({ type: scopeType, id: scopeId, name: scopeName }), [scopeType, scopeId, scopeName]);
    const forceDetail = searchParams.get('detail') === '1';
    const activeBucket = searchParams.get('bucket') || 'overdue';
    const sortBy = searchParams.get('sort') || 'amount_desc';
    const vpMode = searchParams.get('vp_mode') || 'all';

    // Met à jour les paramètres d'URL. push=true → nouvelle entrée d'historique
    // (drill-down navigable au bouton Retour) ; sinon replace (raffinement de filtre).
    const patchParams = useCallback((updates, { push = false } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === undefined || v === '' || v === false) next.delete(k);
            else next.set(k, v === true ? '1' : String(v));
        });
        setSearchParams(next, { replace: !push });
    }, [searchParams, setSearchParams]);

    const buildParams = useCallback(() => {
        const p = { period: periodType, pipeline, scope_type: scope.type };
        if (scope.id) p.scope_id = scope.id;
        if (periodType === 'month') p.month = monthVal;
        if (periodType === 'year') p.year = yearVal;
        if (periodType === 'week') p.week_start = weekStart;
        if (periodType === 'custom') {
            if (dateFrom) p.date_from = dateFrom;
            if (dateTo) p.date_to = dateTo;
        }
        if (forceDetail) p.detail = true;
        return p;
    }, [periodType, pipeline, scope, monthVal, yearVal, weekStart, dateFrom, dateTo, forceDetail]);

    const load = useCallback(async () => {
        // For specific period types, wait until the value is provided
        if (periodType === 'month' && !monthVal) { setLoading(false); return; }
        if (periodType === 'week' && !weekStart) { setLoading(false); return; }
        if (periodType === 'custom' && !dateFrom && !dateTo) { setLoading(false); return; }
        const params = buildParams();
        const cacheKey = `pilotage:${user?.id || 'anon'}:${JSON.stringify(params)}`;

        // 1) Hydratation INSTANTANÉE depuis le cache mémoire (Map) — zéro coût de parsing,
        //    survit aux démontages/remontages du composant pendant la session SPA.
        //    => le bouton "Retour" réaffiche l'écran immédiatement.
        let hadFresh = false;
        let hydrated = false;
        const mem = getCached(cacheKey);
        if (mem !== undefined) {
            setData(mem);
            dataRef.current = true;
            dataKeyRef.current = cacheKey;
            setLoading(false);
            setError(false);
            hadFresh = isFresh(cacheKey);
            hydrated = true;
        } else {
            // Fallback persistant (rechargement de page) : parse localStorage UNE fois puis
            //  on réchauffe le cache mémoire pour les navigations suivantes.
            try {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    setData(parsed);
                    setCached(cacheKey, parsed);
                    dataRef.current = true;
                    dataKeyRef.current = cacheKey;
                    setLoading(false);
                    setError(false);
                    hydrated = true;
                }
            } catch { /* cache illisible : on ignore */ }
        }

        // 2) Cache mémoire FRAIS (< TTL) → on NE refetch PAS : retour réellement instantané,
        //    sans second rendu provoqué par la réponse réseau.
        if (hadFresh) return;

        // 3) État de chargement :
        //    - Nouvelle cible sans cache → on efface l'écran précédent pour afficher des
        //      SKELETONS (et non l'ancien écran / un écran vide qui donne l'impression que "rien ne s'affiche").
        //    - Sinon (mêmes données déjà à l'écran) → refresh discret.
        setError(false);
        if (!hydrated && dataKeyRef.current !== cacheKey) {
            setData(null);
            dataRef.current = false;
            setLoading(true);
        } else if (dataRef.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        try {
            const res = await dashboardAPI.getPilotage(params);
            setData(res.data);
            setCached(cacheKey, res.data);
            dataRef.current = true;
            dataKeyRef.current = cacheKey;
            try { localStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch { /* quota plein : on ignore */ }
        } catch (e) {
            console.error('Pilotage load error', e);
            setError(true); // message + bouton Réessayer plutôt qu'un spinner sans fin
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [buildParams, periodType, monthVal, weekStart, dateFrom, dateTo, user?.id]);

    useEffect(() => {
        // Debounce léger SEULEMENT pour le réseau : l'hydratation cache est immédiate dans load().
        const t = setTimeout(() => { load(); }, 120);
        return () => clearTimeout(t);
    }, [load]);

    const summary = data?.summary || {};
    const teams = data?.teams || [];
    const buckets = data?.buckets || {};
    const hasDetail = !!data?.has_detail;

    // Filtre segment : Réseau — sépare Apporteurs (origin=apporteur) et Parrainage (origin=parrainage).
    // Persisté dans l'URL pour partage et navigation. 'all' par défaut = pas de filtre.
    // NB : ce filtre agit uniquement sur la LISTE des dossiers. Les KPIs financiers sont
    // désormais affichés en DEUX blocs distincts (Apporteurs / Parrainage) — voir plus bas.
    const networkFilter = searchParams.get('network') || 'all';
    const setNetworkFilter = (v) => {
        const np = new URLSearchParams(searchParams);
        if (v === 'all') np.delete('network'); else np.set('network', v);
        setSearchParams(np, { replace: true });
    };

    // Label financier 'Niveau 1' selon le rôle (un VP peut être payeur ET superviseur → cumulé)
    const isSupervisor = teams.some((t) => t.supervisor_vp?.id === user?.id);
    const isPayer = teams.some((t) => t.payer_vp?.id === user?.id);
    const financeLabel = isAdmin
        ? 'Mon résultat Valnexo'
        : user?.role === 'vp'
            ? (isSupervisor && isPayer ? 'Ma part (VP + Superviseur)' : isSupervisor ? 'Ma part Superviseur' : 'Ma part VP Payeur')
            : 'Ma commission';
    // Montants 'ma part' (fallback sur les totaux si l'API ne les renvoie pas encore)
    const myWon = summary.my_won_amount ?? summary.won_amount;
    const myPending = summary.my_pending_amount ?? summary.pending_amount;
    const myOverdue = summary.my_overdue_amount ?? summary.overdue_amount;
    // Dissociation VP Payeur / VP Superviseur — visible uniquement si le VP cumule les 2 rôles
    // sur des dossiers différents (both > 0). Pas d'affichage si mono-rôle : évite le bruit visuel.
    const myWonAsPayer = summary.my_won_as_payer_amount || 0;
    const myWonAsSup = summary.my_won_as_supervisor_amount || 0;
    const myPendingAsPayer = summary.my_pending_as_payer_amount || 0;
    const myPendingAsSup = summary.my_pending_as_supervisor_amount || 0;
    const showWonSplit = user?.role === 'vp' && myWonAsPayer > 0 && myWonAsSup > 0;
    const showPendingSplit = user?.role === 'vp' && myPendingAsPayer > 0 && myPendingAsSup > 0;

    // Lignes du bucket actif, triées selon le critère choisi.
    // 'pending' = vue combinée « En attente » = en cours + en retard (correspond au cadre financier).
    // useMemo : le tri (potentiellement lourd) ne se recalcule que si les données ou le tri changent
    // — évite de re-trier à chaque rendu (frappe, hover, etc.) et fluidifie le « Retour ».
    const activeRows = useMemo(() => {
        const pendingRows = [...(buckets.in_progress || []), ...(buckets.overdue || [])];
        if (activeBucket === 'pending') return pendingRows;
        if (activeBucket === 'pending_close') return pendingRows.filter(dossierHasCommission);
        if (activeBucket === 'pending_advance') return pendingRows.filter((d) => !dossierHasCommission(d));
        return buckets[activeBucket] || [];
    }, [activeBucket, buckets]);
    const [hashtagFilter, setHashtagFilter] = useState([]);
    const detailRows = useMemo(() => {
        let rows = sortDossiers(activeRows, sortBy);
        if (hashtagFilter.length > 0) {
            rows = rows.filter((d) => (d.hashtags || []).some((id) => hashtagFilter.includes(id)));
        }
        // Segment Réseau : Apporteurs classiques vs Parrainage (règle : "origin" du dossier)
        if (networkFilter === 'apporteurs') {
            rows = rows.filter((d) => (d.origin || 'apporteur') !== 'parrainage');
        } else if (networkFilter === 'parrainage') {
            rows = rows.filter((d) => d.origin === 'parrainage');
        }
        // Vue VP : filtre « En direct » (dossiers signés seul) vs « Via apporteurs »
        if (scope.type === 'vp' && vpMode !== 'all') {
            rows = rows.filter((d) => (vpMode === 'direct' ? !d.apporteur : !!d.apporteur));
        }
        return rows;
    }, [activeRows, sortBy, hashtagFilter, scope.type, vpMode, networkFilter]);
    const activeBucketCount = activeBucket === 'pending'
        ? (summary.in_progress ?? 0) + (summary.overdue ?? 0)
        : (activeBucket === 'pending_close' ? (summary.pending_close ?? 0)
            : (activeBucket === 'pending_advance' ? (summary.pending_advance ?? 0)
                : (summary[activeBucket] ?? 0)));

    // Tri des membres d'équipe précalculé une seule fois par jeu de données (vue d'ensemble).
    const sortedTeams = useMemo(() => teams.map((t) => ({
        ...t,
        sortedMembers: [...(t.members || [])].sort(
            (a, b) => sumBd(b.breakdown?.won) - sumBd(a.breakdown?.won)
                || sumBd(b.breakdown?.pending) - sumBd(a.breakdown?.pending)
        ),
    })), [teams]);

    // « À aller chercher » (vue management) : agrège les commissions EN ATTENTE visibles par
    // le rôle, en parts distinctes (RBAC), + top apporteurs à relancer. Sert l'encart motivant.
    const aChercher = useMemo(() => {
        const byKey = {};
        const tops = [];
        (teams || []).forEach((t) => {
            (t.totals?.pending || []).forEach((it) => {
                const e = byKey[it.key] || { key: it.key, label: it.label, value: 0 };
                e.value += it.value;
                byKey[it.key] = e;
            });
            (t.members || []).forEach((m) => {
                const pend = m.breakdown?.pending || [];
                const appPart = ((pend.find((i) => i.key === 'apporteur') || {}).value || 0)
                    + ((pend.find((i) => i.key === 'parrain') || {}).value || 0);
                if (appPart > 0) tops.push({ id: m.id, name: m.name, value: appPart, count: m.breakdown?.pending_com_count || 0 });
            });
        });
        const parts = ['valnexo', 'vp_sup', 'vp', 'apporteur', 'parrain']
            .filter((k) => byKey[k] && byKey[k].value > 0)
            .map((k) => ({ ...byKey[k], label: k === 'apporteur' ? 'Apporteurs' : (k === 'parrain' ? 'Parrains' : byKey[k].label) }));
        const total = parts.reduce((s, p) => s + p.value, 0);
        tops.sort((a, b) => b.value - a.value);
        return { parts, total, tops: tops.slice(0, 4) };
    }, [teams]);
    const pendingCount = (summary.in_progress || 0) + (summary.overdue || 0);
    // ============================================================
    // Issue 3 — En-tête profil quand on cible un apporteur / VP :
    // photo, email, téléphone (vue management « 360° »). Chargé depuis le
    // cache (instantané si déjà préchargé au survol via la fiche).
    // ============================================================
    const [scopeProfile, setScopeProfile] = useState(null);
    useEffect(() => {
        const isUserScope = (scope.type === 'apporteur' || scope.type === 'vp') && scope.id;
        if (!isUserScope) { setScopeProfile(null); return; }
        let cancelled = false;
        const key = `pilotage-userhdr:${scope.id}`;
        // Affichage instantané si déjà en cache, sinon fetch léger.
        const cached = getCached(key);
        if (cached) setScopeProfile(cached);
        swrFetch(key, async () => (await usersAPI.getProfile(scope.id)).data)
            .then((p) => { if (!cancelled) setScopeProfile(p); })
            .catch(() => { if (!cancelled && !cached) setScopeProfile(null); });
        return () => { cancelled = true; };
    }, [scope.type, scope.id]);

    // ============================================================
    // Rendu progressif de la liste de dossiers : le backend renvoie jusqu'à
    // 300 lignes/bucket. Tout monter d'un coup (300 cartes) bloque le paint et
    // alourdit le « Retour ». On affiche 40 lignes puis on étend au scroll.
    // ============================================================
    const DETAIL_PAGE = 40;
    const [visibleCount, setVisibleCount] = useState(DETAIL_PAGE);
    useEffect(() => { setVisibleCount(DETAIL_PAGE); }, [activeBucket, scope.id, scope.type, sortBy, hashtagFilter]);
    const visibleRows = useMemo(() => detailRows.slice(0, visibleCount), [detailRows, visibleCount]);
    const onDetailScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 250) {
            setVisibleCount((c) => (c < detailRows.length ? c + DETAIL_PAGE : c));
        }
    }, [detailRows.length]);

    // Quand on entre dans une CIBLE (clic apporteur/équipe), sélectionner le 1er bucket non vide.
    // On NE touche PAS au bucket choisi explicitement via un clic KPI (« ni plus ni moins »).
    useEffect(() => {
        if (!hasDetail) return;
        if (!autoPickRef.current) return;
        autoPickRef.current = false;
        const pendingLen = (buckets.in_progress || []).length + (buckets.overdue || []).length;
        const curLen = activeBucket.startsWith('pending') ? pendingLen : (buckets[activeBucket] || []).length;
        if (curLen > 0) return;
        const order = ['overdue', 'auto_lost', 'awaiting_install', 'in_progress', 'won', 'lost'];
        const firstNonEmpty = order.find((b) => (buckets[b] || []).length > 0);
        if (firstNonEmpty && firstNonEmpty !== activeBucket) patchParams({ bucket: firstNonEmpty });
    }, [hasDetail, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectScope = (type, id, name, extra = {}) => {
        // à l'entrée d'une cible, on peut auto-choisir un bucket non vide — sauf si un bucket est imposé
        autoPickRef.current = !extra.bucket;
        patchParams({ scope_type: type, scope_id: id, scope_name: name, detail: null, bucket: 'overdue', vp_mode: null, ...extra }, { push: true });
    };
    const resetScope = () => {
        patchParams({ scope_type: null, scope_id: null, scope_name: null, detail: null, bucket: null, vp_mode: null }, { push: true });
    };
    // Clic sur un KPI : ouvre EXACTEMENT la liste des dossiers de ce cadre (ni plus ni moins),
    // puis scrolle vers la liste.
    // `network` optionnel : force le filtre segment (apporteurs / parrainage) — utilisé par
    // le second bloc de KPI « Réseau Parrainage ». On centralise TOUTES les mutations d'URL
    // dans un unique patchParams pour éviter la race condition setSearchParams (stale ref).
    const openBucket = (b, network) => {
        autoPickRef.current = false;
        const updates = { bucket: b };
        if (scope.type === 'all') updates.detail = true;
        if (network === 'parrainage' || network === 'apporteurs') {
            updates.network = network;
        } else if (network === 'all') {
            updates.network = null;
        }
        patchParams(updates, { push: true });
        setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
    };

    const yearOptions = [];
    const cy = new Date().getFullYear();
    for (let y = cy; y >= cy - 6; y--) yearOptions.push(String(y));

    // Callback stable → DossierCard (memo) ne re-render pas inutilement au scroll.
    // detailRowsRef pour capturer la liste au moment du clic (sans dépendance qui invaliderait le callback).
    const detailRowsRef = useRef([]);
    useEffect(() => { detailRowsRef.current = detailRows; }, [detailRows]);
    const openDossier = useCallback((id, pipeline) => {
        // 1) Sauvegarde de l'URL + scroll pour un retour intelligent
        try {
            const path = window.location.pathname + window.location.search;
            sessionStorage.setItem('valnexo_return_state', JSON.stringify({
                path, scroll: window.scrollY || 0, at: Date.now(),
            }));
        } catch { /* noop */ }
        // 2) Snapshot de la liste courante pour navigation Prev/Next
        try {
            const rows = detailRowsRef.current || [];
            if (rows.length > 0) {
                // Déduplique les IDs (un même prospect peut apparaître pour plusieurs pipelines dans une bucket)
                const seen = new Set();
                const uniqueIds = [];
                for (const r of rows) {
                    const rid = r.prospect_id || r.id;
                    if (rid && !seen.has(rid)) {
                        seen.add(rid);
                        uniqueIds.push(rid);
                    }
                }
                if (uniqueIds.length > 0) {
                    saveProspectListContext(
                        uniqueIds,
                        id,
                        { path: window.location.pathname + window.location.search, label: 'Retour au Pilotage', pipeline }
                    );
                }
            }
        } catch { /* noop */ }
        navigate(`/prospects/${id}${pipeline ? `?module=${pipeline}` : ''}`);
    }, [navigate]);
    // Clic sur une pastille # d'une carte → applique/retire ce # au filtre de la liste.
    const toggleHashtag = useCallback((id) => {
        setHashtagFilter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);

    return (
        <div className="space-y-6" data-testid="pilotage-dashboard">
            {/* Breadcrumb « Retour à toutes les équipes » — brief fév. 2026 :
                depuis une équipe précise, on doit pouvoir revenir en un clic à la
                vue globale. Le bouton est TRÈS visible en haut de page. */}
            {(scope.type !== 'all' || forceDetail) && (
                <button
                    type="button"
                    onClick={resetScope}
                    data-testid="pilotage-breadcrumb-back"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-violet-300 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-800 hover:bg-violet-100 hover:border-violet-400 shadow-sm transition-all"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Toutes les équipes
                    {scope.name && (
                        <span className="text-slate-500 font-normal">
                            · vous êtes sur <span className="font-semibold text-slate-800">{scope.name}</span>
                        </span>
                    )}
                </button>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                        Pilotage, {user?.first_name}
                    </h1>
                    <p className="text-slate-600 mt-1 text-sm">
                        {isAdmin ? "Vue d'ensemble société — équipes, dossiers et montants" : 'Pilotez vos équipes et leurs dossiers'}
                    </p>
                </div>
            </div>

            {/* Filtres */}
            <Card className="border-slate-200">
                <CardContent className="p-4 flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-500">Période</label>
                        <Select value={periodType} onValueChange={(v) => { patchParams({ period: v === 'all' ? null : v, from: null, to: null }); }}>
                            <SelectTrigger className="w-[180px]" data-testid="period-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PERIOD_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {periodType === 'custom' && (
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-slate-500">Du</label>
                                <input type="date" value={dateFrom} onChange={(e) => patchParams({ from: e.target.value })}
                                    data-testid="date-from-input"
                                    className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-slate-500">Au</label>
                                <input type="date" value={dateTo} onChange={(e) => patchParams({ to: e.target.value })}
                                    data-testid="date-to-input"
                                    className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
                            </div>
                        </>
                    )}

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-500">Module</label>
                        <Select value={pipeline} onValueChange={(v) => patchParams({ pipeline: v === 'all' ? null : v })}>
                            <SelectTrigger className="w-[170px]" data-testid="pipeline-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PIPELINE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {(scope.type !== 'all' || forceDetail) && (
                        <Button variant="ghost" size="sm" onClick={resetScope} data-testid="reset-scope-btn" className="gap-1 text-purple-700">
                            <ArrowLeft className="h-4 w-4" /> Retour vue d'ensemble
                        </Button>
                    )}
                    {refreshing && (
                        <span className="flex items-center gap-1.5 text-xs text-slate-400 ml-auto" data-testid="pilotage-refreshing">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Actualisation…
                        </span>
                    )}
                </CardContent>
            </Card>

            {/* Erreur de chargement : message clair + bouton Réessayer (jamais de spinner sans fin) */}
            {error && !loading && !refreshing && (
                <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm" data-testid="pilotage-error">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                    <span className="text-amber-800 flex-1">Impossible de charger les données (délai dépassé). Vérifiez votre connexion puis réessayez.</span>
                    <Button size="sm" variant="outline" onClick={() => load()} className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="pilotage-retry-btn">
                        <RotateCcw className="h-4 w-4" /> Réessayer
                    </Button>
                </div>
            )}

            {/* Fil de commissions temps réel (parrains uniquement) — polling 30 s */}
            {user?.role === 'parrain' && <ParrainCommissionFeed user={user} />}

            {/* Encart MANAGEMENT « À aller chercher » (VP & Admin, vue d'ensemble) :
                montant en attente par parts distinctes + top apporteurs à relancer.
                Cliquable → ouvre la liste des dossiers à closer. */}
            {!hasDetail && (isAdmin || user?.role === 'vp') && (aChercher.total > 0 || pendingCount > 0) && (
                <TooltipProvider delayDuration={200}>
                    <Card data-testid="pilotage-a-chercher-card"
                        className="relative overflow-hidden border-slate-200 bg-white transition-shadow hover:shadow-md">
                        {/* Accent latéral violet — signature Valnexo sans écraser */}
                        <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-violet-500 to-purple-600" aria-hidden="true" />
                        <CardContent className="p-4 sm:p-5 pl-5 sm:pl-6">
                            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
                                {/* Bloc principal : montant + parts + CTAs actionnables */}
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="p-2 rounded-lg bg-violet-50 shrink-0">
                                        <Target className="h-5 w-5 text-violet-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">À aller chercher avec {isAdmin ? 'tes équipes' : 'ton équipe'}</p>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button type="button" aria-label="Explication" data-testid="pilotage-a-chercher-help"
                                                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 text-[10px] font-bold leading-none transition-colors">?</button>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                                                    <strong>Le potentiel de commissions à débloquer</strong> pour {isAdmin ? 'les équipes' : 'ton équipe'} : dossiers avec commission renseignée mais pas encore signés (« à closer »), plus les dossiers en cours à faire avancer.
                                                </TooltipContent>
                                            </Tooltip>
                                        </div>
                                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight mt-0.5" data-testid="pilotage-a-chercher-amount">{euro(aChercher.total)}</p>
                                        {aChercher.parts.length > 0 && (
                                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                                {aChercher.parts.map((p) => (
                                                    <span key={p.key} className="text-[11px] text-slate-500">
                                                        {p.label}&nbsp;: <span className="font-semibold text-slate-700">{euro(p.value)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {/* CTAs actionnables — vrais boutons couleurs distinctes */}
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            {aChercher.total > 0 && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => openBucket('pending')}
                                                            data-testid="pilotage-link-closer"
                                                            className="group inline-flex items-center gap-1.5 rounded-full bg-teal-600 hover:bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:shadow transition-all"
                                                        >
                                                            <Wallet className="h-3.5 w-3.5" />
                                                            <span>Dossiers à closer</span>
                                                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                                                        <strong>Argent déjà à portée.</strong> Ces dossiers ont une commission connue mais ne sont pas encore signés. Relance tes apporteurs pour déclencher le paiement.
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                            {pendingCount > 0 && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => openBucket('in_progress')}
                                                            data-testid="pilotage-link-avancer"
                                                            className="group inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:shadow transition-all"
                                                        >
                                                            <TrendingUp className="h-3.5 w-3.5" />
                                                            <span>{pendingCount} à faire avancer</span>
                                                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                                                        <strong>La commission de demain.</strong> Dossiers en cours dont la commission n&apos;est pas encore fixée. Pousse-les pour créer de nouveaux revenus.
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Top apporteurs à relancer — épuré, fond gris clair */}
                                {aChercher.tops.length > 0 && (
                                    <div className="lg:w-72 shrink-0 rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid="pilotage-a-chercher-tops">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 px-1">À relancer en priorité</p>
                                        <div className="space-y-0.5">
                                            {aChercher.tops.map((a) => (
                                                <Tooltip key={a.id}>
                                                    <TooltipTrigger asChild>
                                                        <button type="button"
                                                            onClick={() => selectScope('apporteur', a.id, a.name)}
                                                            data-testid={`a-chercher-top-${a.id}`}
                                                            className="w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-white transition-colors text-left group">
                                                            <span className="text-sm font-medium text-slate-700 group-hover:text-violet-700 truncate">{a.name}</span>
                                                            <span className="text-right shrink-0">
                                                                <span className="block text-sm font-bold text-slate-900">{euro(a.value)}</span>
                                                                {a.count > 0 && <span className="block text-[10px] text-slate-500">{a.count} dossier{a.count > 1 ? 's' : ''} à pousser</span>}
                                                            </span>
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="left" className="max-w-xs text-xs leading-relaxed">
                                                        <strong>{a.name}</strong> — {euro(a.value)} de commission en attente sur {a.count} dossier{a.count > 1 ? 's' : ''}. Clique pour filtrer le tableau de bord sur cet apporteur.
                                                    </TooltipContent>
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TooltipProvider>
            )}

            {(isAdmin || user?.role === 'apporteur' || hasDetail) && (
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-purple-600" />
                    <h2 className="text-base font-semibold text-slate-800" data-testid="finance-role-label">{financeLabel}</h2>
                    <span className="text-xs text-slate-400">(selon période &amp; module sélectionnés)</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <button type="button" onClick={() => openBucket('won')} data-testid="amount-card-won"
                        className="text-left rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-emerald-700 flex items-center gap-1"><Trophy className="h-4 w-4" /> Gagné</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-won">{loading && !data ? '…' : euro(myWon)}</p>
                        {showWonSplit && (
                            <p className="text-[11px] text-slate-500 mt-1 leading-tight" data-testid="amount-won-split">
                                dont VP Payeur&nbsp;: <span className="font-semibold text-slate-700">{euro(myWonAsPayer)}</span>
                                <br />dont Superviseur&nbsp;: <span className="font-semibold text-purple-700">{euro(myWonAsSup)}</span>
                            </p>
                        )}
                    </button>
                    <button type="button" onClick={() => openBucket('pending_close')} data-testid="amount-card-close"
                        className={`text-left rounded-xl border p-4 transition-all hover:shadow-md hover:-translate-y-0.5 ${activeBucket === 'pending_close' && hasDetail ? 'border-teal-400 ring-2 ring-teal-300 bg-teal-50' : 'border-teal-200 bg-gradient-to-br from-teal-50 to-white'}`}>
                        <p className="text-xs font-semibold uppercase text-teal-700 flex items-center gap-1"><Wallet className="h-4 w-4" /> À closer</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-pending">{loading && !data ? '…' : euro(myPending)}</p>
                        <p className="text-xs text-slate-500">{loading && !data ? '' : `${summary.pending_close ?? 0} dossier(s) · commission déjà là`}</p>
                        {showPendingSplit && (
                            <p className="text-[11px] text-slate-500 mt-1 leading-tight" data-testid="amount-pending-split">
                                dont VP Payeur&nbsp;: <span className="font-semibold text-slate-700">{euro(myPendingAsPayer)}</span>
                                <br />dont Superviseur&nbsp;: <span className="font-semibold text-purple-700">{euro(myPendingAsSup)}</span>
                            </p>
                        )}
                    </button>
                    <button type="button" onClick={() => openBucket('pending_advance')} data-testid="amount-card-advance"
                        className={`text-left rounded-xl border p-4 transition-all hover:shadow-md hover:-translate-y-0.5 ${activeBucket === 'pending_advance' && hasDetail ? 'border-blue-400 ring-2 ring-blue-300 bg-blue-50' : 'border-blue-200 bg-gradient-to-br from-blue-50 to-white'}`}>
                        <p className="text-xs font-semibold uppercase text-blue-700 flex items-center gap-1"><TrendingUp className="h-4 w-4" /> À faire avancer</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="count-advance">{loading && !data ? '…' : (summary.pending_advance ?? 0)}</p>
                        <p className="text-xs text-slate-500">dossiers sans commission chiffrée — <strong className="text-blue-700">potentiel probablement bien plus élevé</strong></p>
                    </button>
                    <button type="button" onClick={() => openBucket('overdue')} data-testid="amount-card-overdue"
                        className="text-left rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-amber-700 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> En retard</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-overdue">{loading && !data ? '…' : euro(myOverdue)}</p>
                    </button>
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1.5 pt-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-teal-500" /> À closer = l'argent déjà à portée
                    <span className="mx-1 text-slate-300">•</span>
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> À faire avancer = la commission de demain — <strong>les deux comptent dans ton pilotage</strong>.
                </p>
            </div>
            )}

            {/* ============================================================
                2ᵈ BLOC : Réseau Parrainage — identique en tout point au bloc
                Apporteurs ci-dessus, mais alimenté par les sous-totaux `*_parrainage`.
                Visible pour Admin, VP, apporteur/parrain, ou dès qu'un détail existe —
                à condition qu'il y ait AU MOINS 1 dossier parrainage visible.
                ============================================================ */}
            {((summary.won_parrainage || 0) + (summary.in_progress_parrainage || 0) + (summary.overdue_parrainage || 0) > 0) && (
            <div className="space-y-2" data-testid="finance-parrainage-block">
                <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-indigo-600" />
                    <h2 className="text-base font-semibold text-slate-800">{financeLabel} — <span className="text-indigo-700">Réseau Parrainage</span></h2>
                    <span className="text-xs text-slate-400">(selon période &amp; module sélectionnés)</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <button type="button" onClick={() => openBucket('won', 'parrainage')} data-testid="amount-card-won-parrainage"
                        className="text-left rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-emerald-700 flex items-center gap-1"><Trophy className="h-4 w-4" /> Gagné</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-won-parrainage">
                            {loading && !data ? '…' : euro(summary.my_won_amount_parrainage ?? summary.won_amount_parrainage ?? 0)}
                        </p>
                        <p className="text-xs text-slate-500">{loading && !data ? '' : `${summary.won_parrainage ?? 0} dossier(s) parrainage`}</p>
                    </button>
                    <button type="button" onClick={() => openBucket('pending_close', 'parrainage')} data-testid="amount-card-close-parrainage"
                        className="text-left rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-teal-700 flex items-center gap-1"><Wallet className="h-4 w-4" /> À closer</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-pending-parrainage">
                            {loading && !data ? '…' : euro(summary.my_pending_amount_parrainage ?? summary.pending_amount_parrainage ?? 0)}
                        </p>
                        <p className="text-xs text-slate-500">{loading && !data ? '' : `${summary.pending_close_parrainage ?? 0} dossier(s) · commission déjà là`}</p>
                    </button>
                    <button type="button" onClick={() => openBucket('pending_advance', 'parrainage')} data-testid="amount-card-advance-parrainage"
                        className="text-left rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-blue-700 flex items-center gap-1"><TrendingUp className="h-4 w-4" /> À faire avancer</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="count-advance-parrainage">
                            {loading && !data ? '…' : (summary.pending_advance_parrainage ?? 0)}
                        </p>
                        <p className="text-xs text-slate-500">dossiers parrainage à pousser</p>
                    </button>
                    <button type="button" onClick={() => openBucket('overdue', 'parrainage')} data-testid="amount-card-overdue-parrainage"
                        className="text-left rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5">
                        <p className="text-xs font-semibold uppercase text-amber-700 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> En retard</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="amount-overdue-parrainage">
                            {loading && !data ? '…' : euro(summary.my_overdue_amount_parrainage ?? summary.overdue_amount_parrainage ?? 0)}
                        </p>
                    </button>
                </div>
            </div>
            )}

            {/* Compteurs buckets (cliquables) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {['won', 'awaiting_install', 'in_progress', 'overdue', 'lost', 'auto_lost'].map((b) => (
                    <SummaryCard key={b} bucketKey={b} value={loading && !data ? '…' : (summary[b] ?? 0)}
                        activeBucket={hasDetail ? activeBucket : null}
                        hasDetail={true}
                        onClick={() => openBucket(b)} />
                ))}
            </div>

            {/* Scope banner */}
            {hasDetail && (
                <div className="flex items-center gap-2 text-sm bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="scope-banner">
                    <Search className="h-4 w-4 text-purple-600" />
                    <span className="text-slate-700">Sélection : <span className="font-semibold text-purple-800">{scope.name || (forceDetail ? 'Toute la société' : scope.type)}</span></span>
                </div>
            )}

            {/* En-tête profil (apporteur / VP ciblé) — vue management 360° */}
            {hasDetail && (scope.type === 'apporteur' || scope.type === 'vp') && scopeProfile && (
                <ScopeProfileHeader profile={scopeProfile} onOpenFiche={() => navigate(`/dashboard/team/user/${scopeProfile.id}`)} />
            )}

            {/* Détail des dossiers (si une cible est sélectionnée) */}
            {hasDetail ? (
                <Card className="border-slate-200" ref={detailRef}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                                {['won', 'awaiting_install', 'pending', 'in_progress', 'overdue', 'lost', 'auto_lost'].map((b) => {
                                    const meta = BUCKET_META[b];
                                    const Icon = meta.icon;
                                    const count = b === 'pending'
                                        ? (summary.in_progress ?? 0) + (summary.overdue ?? 0) + (summary.awaiting_install ?? 0)
                                        : (summary[b] ?? (buckets[b] || []).length);
                                    return (
                                        <button key={b} type="button" data-testid={`bucket-tab-${b}`}
                                            onClick={() => { autoPickRef.current = false; patchParams({ bucket: b }, { push: true }); }}
                                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium border transition-all
                                                ${activeBucket === b ? 'border-purple-500 bg-purple-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-purple-300'}`}>
                                            <Icon className="h-3.5 w-3.5" /> {meta.label}
                                            <span className={`text-[11px] rounded-full px-1.5 ${activeBucket === b ? 'bg-white/20' : 'bg-slate-100'}`}>{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {/* Tri intelligent (priorité économique) */}
                            <div className="flex items-center gap-2">
                                <HashtagFilter selected={hashtagFilter} onChange={setHashtagFilter} className="h-9 w-[150px]" />
                                {hashtagFilter.length > 0 && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 whitespace-nowrap" data-testid="hashtag-match-count">
                                        <Hash className="w-3 h-3" />{detailRows.length} dossier{detailRows.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                                <ArrowUpDown className="h-4 w-4 text-slate-400" />
                                <Select value={sortBy} onValueChange={(v) => patchParams({ sort: v === 'amount_desc' ? null : v })}>
                                    <SelectTrigger className="h-9 w-[200px]" data-testid="dossier-sort-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {scope.type === 'vp' && (
                            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="vp-mode-filter">
                                <span className="text-xs text-slate-400">Vue&nbsp;:</span>
                                {[['all', 'Tous'], ['direct', 'En direct'], ['indirect', 'Via apporteurs']].map(([v, label]) => (
                                    <button key={v} type="button" data-testid={`vp-mode-${v}`}
                                        onClick={() => patchParams({ vp_mode: v === 'all' ? null : v })}
                                        className={`rounded-full px-3 py-1 text-xs font-medium border transition-all
                                            ${vpMode === v ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Filtre segment Réseau : dissocie apporteurs classiques et parrainage */}
                        <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="network-filter">
                            <span className="text-xs text-slate-400">Réseau&nbsp;:</span>
                            {[['all', 'Tous'], ['apporteurs', 'Apporteurs'], ['parrainage', 'Parrainage']].map(([v, label]) => (
                                <button key={v} type="button" data-testid={`network-${v}`}
                                    onClick={() => setNetworkFilter(v)}
                                    className={`rounded-full px-3 py-1 text-xs font-medium border transition-all
                                        ${networkFilter === v ? (v === 'parrainage' ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-emerald-500 bg-emerald-600 text-white') : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        {activeBucket === 'auto_lost' && (buckets.auto_lost || []).length > 0 && (
                            <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                Ces dossiers sont passés automatiquement en perdu après 15 jours d'inactivité sur la même étape (Courtage).
                                Ouvrez-les pour les réattribuer à un apporteur et les relancer.
                            </div>
                        )}

                        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="dossier-list" onScroll={onDetailScroll}>
                            {loading && !data ? (
                                [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)
                            ) : detailRows.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-8">Aucun dossier dans cette catégorie pour la période sélectionnée.</p>
                            ) : activeBucket === 'pending' ? (
                                // Vue « À aller chercher » scindée en 2 listes exploitables :
                                // 1) dossiers avec commission chiffrée (à CLOSER) ; 2) sans commission (à FAIRE AVANCER).
                                (() => {
                                    const withCom = visibleRows.filter(dossierHasCommission);
                                    const withoutCom = visibleRows.filter((d) => !dossierHasCommission(d));
                                    return (
                                        <>
                                            <div className="flex items-center gap-2 pt-1 pb-1" data-testid="pending-section-close">
                                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1">
                                                    <Wallet className="w-3.5 h-3.5" /> À CLOSER — commission à aller chercher ({withCom.length})
                                                </span>
                                            </div>
                                            {withCom.length === 0 ? (
                                                <p className="text-xs text-slate-400 px-1 pb-1">Aucun dossier avec commission chiffrée pour l'instant.</p>
                                            ) : withCom.map((d) => (
                                                <DossierCard key={`c-${d.line_id || `${d.id}-${d.pipeline}`}`} d={d} onOpen={openDossier} onTagClick={toggleHashtag} />
                                            ))}
                                            <div className="flex items-center gap-2 pt-3 pb-1" data-testid="pending-section-advance">
                                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-100 rounded-full px-2.5 py-1">
                                                    <Clock className="w-3.5 h-3.5" /> À FAIRE AVANCER — pas encore de commission ({withoutCom.length})
                                                </span>
                                            </div>
                                            {withoutCom.length === 0 ? (
                                                <p className="text-xs text-slate-400 px-1">Aucun dossier à faire avancer ici.</p>
                                            ) : withoutCom.map((d) => (
                                                <DossierCard key={`a-${d.line_id || `${d.id}-${d.pipeline}`}`} d={d} onOpen={openDossier} onTagClick={toggleHashtag} />
                                            ))}
                                        </>
                                    );
                                })()
                            ) : (
                                <>
                                    {(activeBucket === 'pending_close' || activeBucket === 'pending_advance') && (
                                        <div className="flex items-center gap-2 pt-1 pb-1" data-testid={`pending-header-${activeBucket === 'pending_close' ? 'close' : 'advance'}`}>
                                            {activeBucket === 'pending_close' ? (
                                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 bg-teal-100 rounded-full px-2.5 py-1">
                                                    <Wallet className="w-3.5 h-3.5" /> À CLOSER — commission déjà présente ({detailRows.length})
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-100 rounded-full px-2.5 py-1">
                                                    <TrendingUp className="w-3.5 h-3.5" /> À FAIRE AVANCER — pas encore de commission ({detailRows.length})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {visibleRows.map((d) => (
                                        <DossierCard key={`${d.line_id || `${d.id}-${d.pipeline}`}`} d={d} onOpen={openDossier} onTagClick={toggleHashtag} />
                                    ))}
                                </>
                            )}
                            {!loading && visibleCount < detailRows.length && (
                                <p className="text-xs text-slate-400 text-center py-2" data-testid="dossier-load-more-hint">
                                    Affichage de {visibleRows.length} dossiers sur {detailRows.length}. Faites défiler pour en afficher plus.
                                </p>
                            )}
                            {!loading && activeBucketCount > detailRows.length && visibleCount >= detailRows.length && (
                                <p className="text-xs text-slate-400 text-center py-2">
                                    Affichage des {detailRows.length} premiers dossiers sur {activeBucketCount}. Affinez les filtres pour réduire la liste.
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                /* Navigation par équipes — carrousel horizontal (défilement latéral) */
                <div className="space-y-2" data-testid="teams-nav">
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                            <Users className="h-5 w-5 text-purple-600" /> Équipes
                            {teams.length > 0 && (
                                <span className="text-xs text-slate-400 font-normal">({teams.length})</span>
                            )}
                        </h2>
                        {teams.length > 1 && (
                            <span className="text-[11px] text-slate-400 italic hidden sm:inline">
                                ← Fais défiler pour voir les autres équipes →
                            </span>
                        )}
                    </div>
                    {loading && !data ? (
                        <div className="flex gap-3 overflow-hidden">
                            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 min-w-[320px] rounded-2xl" />)}
                        </div>
                    ) : teams.length === 0 ? (
                        <p className="text-sm text-slate-400">Aucune équipe visible.</p>
                    ) : (
                        <div
                            className="relative flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1 scroll-smooth"
                            style={{ scrollbarWidth: 'thin' }}
                            data-testid="teams-carousel"
                        >
                        {sortedTeams.map((t) => {
                            const expanded = expandedTeams[t.id] !== false; // default expanded
                            const c = t.counts || {};
                            const total = (c.won || 0) + (c.in_progress || 0) + (c.overdue || 0) + (c.lost || 0) + (c.auto_lost || 0);
                            return (
                                <Card
                                    key={t.id}
                                    className={`border-slate-200 overflow-hidden snap-start shrink-0 flex flex-col transition-all ${expanded ? 'w-[min(92vw,420px)]' : 'w-[min(88vw,340px)]'}`}
                                    data-testid={`team-card-${t.id}`}
                                >
                                    <CardContent className="p-4 flex-1 flex flex-col">
                                        <div className="flex items-start justify-between gap-2">
                                            <button
                                                type="button"
                                                data-testid={`team-toggle-${t.id}`}
                                                onClick={() => setExpandedTeams((p) => ({ ...p, [t.id]: !expanded }))}
                                                className="flex items-start gap-2 min-w-0 flex-1 text-left"
                                                title={t.name}
                                            >
                                                <span className="shrink-0 mt-0.5">
                                                    {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                                                </span>
                                                <span className="flex-1 min-w-0">
                                                    <span className="block font-semibold text-slate-900 leading-tight break-words">{t.name}</span>
                                                    <span className="text-xs text-slate-400">{total} dossier{total > 1 ? 's' : ''}</span>
                                                </span>
                                            </button>
                                        </div>

                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            {c.overdue > 0 && (
                                                <button type="button" data-testid={`team-badge-overdue-${t.id}`}
                                                    onClick={() => { selectScope('team', t.id, t.name, { bucket: 'overdue' }); setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 450); }}
                                                    className="rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 hover:bg-amber-200 transition-colors cursor-pointer">
                                                    {c.overdue} retard
                                                </button>
                                            )}
                                            {c.auto_lost > 0 && (
                                                <button type="button" data-testid={`team-badge-autolost-${t.id}`}
                                                    onClick={() => { selectScope('team', t.id, t.name, { bucket: 'auto_lost' }); setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 450); }}
                                                    className="rounded-full bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 hover:bg-red-200 transition-colors cursor-pointer">
                                                    {c.auto_lost} inactivité
                                                </button>
                                            )}
                                            <Button size="sm" variant="outline" data-testid={`team-open-${t.id}`}
                                                onClick={() => selectScope('team', t.id, t.name)} className="h-7 text-xs ml-auto">
                                                Voir l&apos;équipe
                                            </Button>
                                        </div>

                                        {expanded && (
                                            <div className="mt-3 space-y-3">
                                                {hasBd(t.totals) && (
                                                    <div className="rounded-xl border border-purple-200 bg-purple-50/60 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                                                        data-testid={`team-totals-${t.id}`}>
                                                        <span className="text-[11px] font-semibold text-purple-700 uppercase tracking-wide">Total équipe</span>
                                                        <BreakdownStats breakdown={t.totals} size="lg" testid={`team-totals-bd-${t.id}`} />
                                                    </div>
                                                )}
                                                <div className="flex flex-wrap gap-3">
                                                    {t.payer_vp && (
                                                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                                            <span className="text-[11px] font-semibold text-slate-400 uppercase">VP</span>
                                                            <PersonChip person={t.payer_vp} testid={`scope-vp-${t.payer_vp.id}`}
                                                                onClick={() => selectScope('vp', t.payer_vp.id, t.payer_vp.name)} />
                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${t.payer_vp.id}`)} userId={t.payer_vp.id} testid={`fiche-vp-${t.payer_vp.id}`} />
                                                        </div>
                                                    )}
                                                    {t.supervisor_vp && (
                                                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                                            <span className="text-[11px] font-semibold text-purple-400 uppercase">Superviseur</span>
                                                            <PersonChip person={t.supervisor_vp} testid={`scope-sup-${t.supervisor_vp.id}`}
                                                                onClick={() => selectScope('vp', t.supervisor_vp.id, t.supervisor_vp.name)} />
                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${t.supervisor_vp.id}`)} userId={t.supervisor_vp.id} testid={`fiche-sup-${t.supervisor_vp.id}`} />
                                                        </div>
                                                    )}
                                                </div>
                                                {t.payer_vp?.line && hasBd(t.payer_vp.line) && (
                                                    <div data-testid={`vp-line-${t.payer_vp.id}`}
                                                        className="rounded-xl border border-indigo-200 bg-indigo-50/50 px-3 py-2.5">
                                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wide shrink-0">Ligne VP</span>
                                                                <button type="button" data-testid={`vp-line-scope-${t.payer_vp.id}`}
                                                                    onClick={() => selectScope('vp', t.payer_vp.id, t.payer_vp.name)}
                                                                    className="flex items-center gap-2 min-w-0 text-left group">
                                                                    <Avatar className="h-6 w-6 border border-indigo-200">
                                                                        {t.payer_vp.avatar && <AvatarImage src={t.payer_vp.avatar} alt={t.payer_vp.name} />}
                                                                        <AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px] font-semibold">{initials(t.payer_vp.name)}</AvatarFallback>
                                                                    </Avatar>
                                                                    <span className="text-sm font-semibold text-slate-800 truncate group-hover:text-indigo-700">{t.payer_vp.name}</span>
                                                                </button>
                                                            </div>
                                                            <BreakdownStats breakdown={t.payer_vp.line} testid={`vp-line-bd-${t.payer_vp.id}`} />
                                                        </div>
                                                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
                                                            <button type="button" data-testid={`vp-line-direct-${t.payer_vp.id}`}
                                                                onClick={() => selectScope('vp', t.payer_vp.id, t.payer_vp.name, { bucket: 'won', vp_mode: 'direct' })}
                                                                className="hover:text-indigo-700 hover:underline transition-colors">
                                                                Gagné en direct <b className="text-slate-700">{euro(t.payer_vp.line.direct?.won || 0)}</b>
                                                            </button>
                                                            <button type="button" data-testid={`vp-line-indirect-${t.payer_vp.id}`}
                                                                onClick={() => selectScope('vp', t.payer_vp.id, t.payer_vp.name, { bucket: 'won', vp_mode: 'indirect' })}
                                                                className="hover:text-indigo-700 hover:underline transition-colors">
                                                                Gagné via apporteurs <b className="text-slate-700">{euro(t.payer_vp.line.indirect?.won || 0)}</b>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {t.members.length > 0 ? (
                                                    <div data-testid={`team-ranking-${t.id}`}>
                                                        <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Classement équipe</p>
                                                        <div className="space-y-1">
                                                            {t.sortedMembers
                                                                .map((m, idx) => {
                                                                    const isParrain = m.role === 'parrain' || m.is_parrain;
                                                                    return (
                                                                    <div key={m.id}
                                                                        className={`w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 rounded-lg border px-2.5 py-1.5 transition-all ${isParrain ? 'border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50/40' : 'border-slate-100 hover:border-purple-200 hover:bg-purple-50/40'}`}>
                                                                        <button type="button" data-testid={`scope-member-${m.id}`}
                                                                            onClick={() => selectScope('apporteur', m.id, m.name)}
                                                                            className="flex items-center gap-2 min-w-0 flex-1 text-left">
                                                                            <span className={`text-[11px] font-bold w-4 text-right ${isParrain ? 'text-indigo-400' : 'text-slate-400'}`}>{idx + 1}</span>
                                                                            <Avatar className={`h-6 w-6 border ${isParrain ? 'border-indigo-200' : 'border-slate-200'}`}>
                                                                                {m.avatar && <AvatarImage src={m.avatar} alt={m.name} />}
                                                                                <AvatarFallback className={`text-[10px] font-semibold ${isParrain ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>{initials(m.name)}</AvatarFallback>
                                                                            </Avatar>
                                                                            <span className="text-sm font-medium text-slate-700 truncate">{m.name}</span>
                                                                            {isParrain && (
                                                                                <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`badge-parrain-${m.id}`}>
                                                                                    Parrain{m.parrain_level ? ` N${m.parrain_level}` : ''}
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                        <div className="flex items-center justify-end gap-2 shrink-0 w-full sm:w-auto pl-6 sm:pl-0">
                                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${m.id}`)} userId={m.id} testid={`fiche-member-${m.id}`} />
                                                                            <BreakdownStats breakdown={m.breakdown} testid={`member-bd-${m.id}`} />
                                                                        </div>
                                                                    </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                ) : (!t.payer_vp && (
                                                    <span className="text-xs text-slate-400">Aucun membre rattaché.</span>
                                                ))}

                                                {/* Le bloc « parrain_members » séparé a été supprimé : les parrains
                                                    apparaissent désormais dans le classement équipe ci-dessus avec un
                                                    badge indigo « Parrain N1/N2/N3 », ce qui évite les doublons visuels
                                                    tout en conservant la distinction claire parrain/apporteur.
                                                    La page /parrainage reste la source de vérité pour la vue Réseau. */}
                                                {false && (t.parrain_members || []).length > 0 && (
                                                    <div data-testid={`team-parrain-ranking-${t.id}`} className="pt-2 mt-2 border-t border-indigo-100">
                                                        <p className="text-[11px] font-semibold text-indigo-500 uppercase mb-1">Réseau Parrainage · classement parrains</p>
                                                        <div className="space-y-1">
                                                            {[...t.parrain_members]
                                                                .sort((a, b) => (b.stats?.won_amount || 0) - (a.stats?.won_amount || 0))
                                                                .map((m, idx) => (
                                                                    <div key={m.id}
                                                                        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 rounded-lg border border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50/50 px-2.5 py-1.5 transition-all">
                                                                        <button type="button" data-testid={`scope-parrain-${m.id}`}
                                                                            onClick={() => selectScope('apporteur', m.id, m.name)}
                                                                            className="flex items-center gap-2 min-w-0 flex-1 text-left">
                                                                            <span className="text-[11px] font-bold text-indigo-400 w-4 text-right">{idx + 1}</span>
                                                                            <Avatar className="h-6 w-6 border border-indigo-200">
                                                                                {m.avatar && <AvatarImage src={m.avatar} alt={m.name} />}
                                                                                <AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px] font-semibold">{initials(m.name)}</AvatarFallback>
                                                                            </Avatar>
                                                                            <span className="text-sm font-medium text-slate-700 truncate">{m.name}</span>
                                                                            {m.parrain_level && (
                                                                                <span className="text-[10px] font-semibold text-indigo-500 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">N{m.parrain_level}</span>
                                                                            )}
                                                                        </button>
                                                                        <div className="flex items-center justify-end gap-2 shrink-0 w-full sm:w-auto pl-6 sm:pl-0">
                                                                            <FicheButton onClick={() => navigate(`/dashboard/team/user/${m.id}`)} userId={m.id} testid={`fiche-parrain-${m.id}`} />
                                                                            <BreakdownStats breakdown={m.breakdown} testid={`parrain-bd-${m.id}`} />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
